'use client';

import { useAppConfiguration } from '@/features/app-config';
import { useTranslation } from '@/features/i18n';
import { Link } from '@/ui/Link';
import { BookmarkIcon } from '@heroicons/react/24/outline';
import {
  BookmarkIcon as BookmarkIconSolid,
  LockClosedIcon,
} from '@heroicons/react/24/solid';
import { useSubprofiles } from '@vimond/features/subprofiles';
import { useWatchlist } from '@vimond/features/watchlist';
import { useLocale } from '@vimond/i18n';
import {
  AssetType,
  CategoryType,
  CuratedAssetType,
  CuratedCategoryType,
} from '@vimond/services/content-discovery';
import { useEffect, useState } from 'react';

type Props = {
  asset?: AssetType | CuratedAssetType;
  category?: CategoryType | CuratedCategoryType;
};

export const AddToWatchListButton = ({ asset, category }: Props) => {
  const { t } = useTranslation();
  const { subprofileToken } = useSubprofiles();
  const { locale } = useLocale();
  const {
    addToWatchlist,
    addToWatchlistStatus,
    isInWatchlist,
    removeFromWatchlist,
    removeFromWatchlistStatus,
  } = useWatchlist(locale);
  const [loading, setLoading] = useState(false);
  const { authenticationEnabled } = useAppConfiguration();

  useEffect(() => {
    if (
      removeFromWatchlistStatus === 'pending' ||
      addToWatchlistStatus === 'pending'
    )
      setLoading(true);
    else setLoading(false);
  }, [removeFromWatchlistStatus, addToWatchlistStatus]);

  if (!authenticationEnabled) return;
  if (!subprofileToken) {
    return (
      <Link href="/auth/signin">
        <div
          className="group relative cursor-pointer"
          title={t(
            'you-need-to-login-before-you-can-add-content-to-your-watchlist',
          )}
        >
          <BookmarkIcon className=" h-11 w-11" />
          <LockClosedIcon className="text-text-highlight absolute bottom-0 right-1 h-5 w-5" />
        </div>
      </Link>
    );
  }

  const addItemToWatchList = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    await addToWatchlist({ asset, category });
  };

  const removeItemFromWatchList = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    await removeFromWatchlist({ asset, category });
  };

  const isInWatchlistState = isInWatchlist({
    assetId: asset?.id,
    categoryId: category?.id,
  });

  if (!isInWatchlistState) {
    return (
      <div
        className="cursor-pointer relative group h-11 w-11"
        onClick={addItemToWatchList}
        title={t('add-to-watchlist')}
      >
        <BookmarkIcon className="h-11 w-11" />
        {loading && (
          <div className="absolute right-0 bottom-0 w-5 h-5 border-2 border-button-primary-border border-t-transparent rounded-full animate-spin [animation-delay:-0.15s]" />
        )}
      </div>
    );
  }

  return (
    <div
      className="cursor-pointer relative group h-11 w-11"
      onClick={removeItemFromWatchList}
      title={t('remove-from-watchlist')}
    >
      <BookmarkIconSolid className="h-11 w-11 text-primary group-hover:text-primary-dark" />
      {loading && (
        <div className="absolute right-0 bottom-0 w-5 h-5 border-2 border-button-primary-border border-t-transparent rounded-full animate-spin [animation-delay:-0.15s]" />
      )}
    </div>
  );
};
